/**
 * Font loading utilities for the Banner Ad Creator.
 * Ensures fonts are fully loaded before rendering to canvas.
 */

/** @type {string[]} */
const REQUIRED_FONTS = [
  'Helvetica',
];

/** @type {Map<string, string>} */
const FONT_WEIGHT_MAP = new Map([
  ['400', 'normal'],
  ['600', 'semibold'],
  ['700', 'bold'],
  ['800', 'extrabold'],
  ['900', 'black'],
  ['1000', 'extrablack'],
]);

/**
 * Wait for all required fonts to be loaded.
 * @returns {Promise<void>}
 */
export async function waitForFonts() {
  if (!document.fonts) {
    // Font Loading API not supported — add a small delay fallback
    return new Promise(resolve => setTimeout(resolve, 500));
  }

  try {
    await document.fonts.ready;

    // Explicitly check each required font at key weights
    const checks = [];
    for (const family of REQUIRED_FONTS) {
      for (const weight of ['400', '600', '700', '900']) {
        checks.push(
          document.fonts.load(`${weight} 16px "${family}"`).catch(() => {
            console.warn(`Font check failed: ${family} weight ${weight}`);
          })
        );
      }
    }
    await Promise.all(checks);
  } catch (err) {
    console.warn('Font loading check failed, proceeding anyway:', err);
  }
}

/**
 * Get a CSS font string suitable for canvas context.
 * @param {object} options
 * @param {string} [options.fontFamily='Helvetica']
 * @param {number} [options.fontSize=24]
 * @param {number|string} [options.fontWeight=400]
 * @param {string} [options.fontStyle='normal']
 * @returns {string}
 */
export function buildCanvasFont({ fontFamily = 'Helvetica', fontSize = 24, fontWeight = 400, fontStyle = 'normal' } = {}) {
  return `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}", sans-serif`;
}

/**
 * Measure text dimensions using a canvas context.
 * @param {string} text
 * @param {string} font - CSS font string
 * @returns {{ width: number, height: number, actualBoundingBoxAscent: number, actualBoundingBoxDescent: number }}
 */
export function measureText(text, font) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.font = font;
  const metrics = ctx.measureText(text);
  return {
    width: metrics.width,
    height: (metrics.actualBoundingBoxAscent || 0) + (metrics.actualBoundingBoxDescent || 0),
    actualBoundingBoxAscent: metrics.actualBoundingBoxAscent || 0,
    actualBoundingBoxDescent: metrics.actualBoundingBoxDescent || 0,
  };
}
